import React, { useEffect, useState, useRef } from 'react';
import { Plus, Download, Search, Check, Upload, FileSpreadsheet, LogIn, LogOut, X } from 'lucide-react';
import { Contributor, Solution, AvailabilityMap, AvailabilityStatus, Region, REGIONS, DateAvailability, DateAvailabilityMap, ExperienceLevel, ExperienceLevelMap, EXPERIENCE_LABELS } from '../types';
import { AvailabilityGrid } from './AvailabilityGrid';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { DateAvailabilityModal } from './DateAvailabilityModal';
import { useAdobeImsAuth } from '../auth/useAdobeImsAuth';
import { logAvailabilityChange, logExperienceLevelChange, logOOOSchedule, logDateOverride, logRegionUpdate, logContributorEdit, logSolutionEdit, fetchInitialStateFromDatabase, type DashboardStatePayload, logStateToDatabase } from '../utils/database';
const INITIAL_SOLUTIONS = ['CSC / GenStudio', 'AEM Sites (EDS, MS, On-Prem)', 'AEM Assets (+Dynamic Media and Content Hub)', 'AEM Forms', 'AEM Sites Optimizer', 'Workfront, WF Planning, WF Fusion', 'GenStudio for Performance Marketing', 'Creative Cloud Integrations', 'AEM Multi-Solution', 'Adobe Commerce', 'LLM Optimizer', 'Brand Concierge', 'Workflow Optimization Agent', 'Adobe Acrobat', 'Adobe Acrobat Sign', 'Adobe Acrobat Studio', 'Firefly Services', 'AEP Overall', 'RTCDP B2B', 'AJO B2B', 'RTCDP B2C', 'AJO B2C', 'CJA (B2C + B2B)', 'RTCDP Connections', 'AEP AI Agents Orchestrator', 'Marketo Engage', 'Marketo Measure', 'Adobe Campaign (v7 + v8)', 'Mix Modeler', 'Adobe Target', 'Adobe Analytics'];

// Baseline data from spreadsheet - format: "status" or "status:level"
const INITIAL_DATA: {
  name: string;
  region: Region;
  data: string[];
}[] = [{
  name: 'Ariana Deng',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Khalil Welch',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Lauren Conn',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Moyo Oyedeji',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Priya Desai',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Robin Rubeck',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Taylor Phillips',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Esha Julka',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Stephen Ario',
  region: 'USA',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Aishwarya Kh',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Arun Nagpal',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Dipen Sagar',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Pradeep Kumar',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Raghav Sridhar',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Vikran VK',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Aniruddhan C',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Ankur Dhaka',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Ashwini N',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Binay Pradhan',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Manu Saxena',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Tanya Arora',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}, {
  name: 'Vishnu Pillai',
  region: 'India',
  data: ['unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable', 'unavailable']
}];
async function stableId(input: string) {
  const bytes = new TextEncoder().encode(input.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}
function stableSlugId(input: string) {
  return input.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Helper to build initial state from INITIAL_DATA
function buildInitialState() {
  // const solutionIds = INITIAL_SOLUTIONS.map(() => crypto.randomUUID());
  // Replacing IDs with fixed sha56 based ids to ensure consistency
  const solutionIds = INITIAL_SOLUTIONS.map(name => stableSlugId(name));
  const solutions: Solution[] = INITIAL_SOLUTIONS.map((name, i) => ({
    id: solutionIds[i],
    name
  }));
  const contributors: Contributor[] = [];
  const availability: AvailabilityMap = {};
  const experienceLevels: ExperienceLevelMap = {};
  const validStatuses = ['available', 'limited', 'unavailable'];
  const validLevels = ['L1', 'L2', 'L3', 'L4'];
  INITIAL_DATA.forEach(({
    name,
    region,
    data
  }) => {
    // const id = crypto.randomUUID();

    const id = stableSlugId(name);
    contributors.push({
      id,
      name,
      region
    });
    availability[id] = {};
    experienceLevels[id] = {};
    data.forEach((cell, idx) => {
      const solutionId = solutionIds[idx];
      if (!solutionId) return;
      const parts = cell.split(':');
      const status = parts[0]?.toLowerCase();
      const level = parts[1]?.toUpperCase();
      availability[id][solutionId] = validStatuses.includes(status) ? status as AvailabilityStatus : 'unavailable';
      if (level && validLevels.includes(level)) {
        experienceLevels[id][solutionId] = level as ExperienceLevel;
      }
    });
  });
  return {
    contributors,
    solutions,
    availability,
    experienceLevels
  };
}
const STORAGE_KEYS = {
  CONTRIBUTORS: 'availability-dashboard-contributors',
  SOLUTIONS: 'availability-dashboard-solutions',
  AVAILABILITY: 'availability-dashboard-availability',
  DATE_AVAILABILITY: 'availability-dashboard-date-availability',
  EXPERIENCE_LEVELS: 'availability-dashboard-experience-levels'
};
function parseStoredValue<T>(key: string, fallback: T): T {
  const saved = localStorage.getItem(key);
  if (!saved) {
    return fallback;
  }
  try {
    return JSON.parse(saved) as T;
  } catch (error) {
    console.error(`Failed to parse saved ${key}`, error);
    return fallback;
  }
}
function getLocalStorageState(): DashboardStatePayload | null {
  const contributors = parseStoredValue<Contributor[]>(STORAGE_KEYS.CONTRIBUTORS, []);
  const solutions = parseStoredValue<Solution[]>(STORAGE_KEYS.SOLUTIONS, []);
  if (contributors.length === 0 && solutions.length === 0) {
    return null;
  }
  return {
    contributors,
    solutions,
    availability: parseStoredValue<AvailabilityMap>(STORAGE_KEYS.AVAILABILITY, {}),
    dateAvailability: parseStoredValue<DateAvailabilityMap>(STORAGE_KEYS.DATE_AVAILABILITY, {}),
    experienceLevels: parseStoredValue<ExperienceLevelMap>(STORAGE_KEYS.EXPERIENCE_LEVELS, {})
  };
}
const STATUS_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Qualified and Available',
  limited: 'Qualified not Available',
  unavailable: 'Not Qualified'
};
const STATUS_COLORS: Record<AvailabilityStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  available: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300'
  },
  limited: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  unavailable: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300'
  }
};
export function Dashboard() {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    accessLevel,
    canManageData,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const isAdmin = accessLevel === 'admin';
  // Filter state
  const [selectedContributorId, setSelectedContributorId] = useState<string | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<AvailabilityStatus | null>(null);
  const [selectedExperienceFilter, setSelectedExperienceFilter] = useState<ExperienceLevel | null>(null);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<Region | null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [availability, setAvailability] = useState<AvailabilityMap>({});
  const [dateAvailability, setDateAvailability] = useState<DateAvailabilityMap>({});
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevelMap>({});
  const [isHydrating, setIsHydrating] = useState(true);
  const hasInitializedContributors = useRef(false);
  const shouldSkipNextSave = useRef(true);
  useEffect(() => {
    let isCancelled = false;
    if (!isAuthReady) {
      setIsHydrating(true);
      return () => {
        isCancelled = true;
      };
    }
    if (!isSignedIn) {
      hasInitializedContributors.current = false;
      shouldSkipNextSave.current = true;
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setDateAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    const loadInitialState = async () => {
      setIsHydrating(true);
      const databaseState = await fetchInitialStateFromDatabase();
      console.log('Loaded databaseState state:', databaseState);
      const localState = getLocalStorageState();
      console.log('Loaded localState state:', localState);
      const initialState = databaseState || localState || buildInitialState();
      console.log('Loaded initial state:', initialState);
      if (isCancelled) {
        return;
      }
      setContributors(initialState.contributors);
      setSolutions(initialState.solutions);
      setAvailability(initialState.availability);
      setDateAvailability(initialState.dateAvailability || {});
      setExperienceLevels(initialState.experienceLevels);
      setIsHydrating(false);
    };
    loadInitialState();
    return () => {
      isCancelled = true;
    };
  }, [isAuthReady, isSignedIn]);
  useEffect(() => {
    if (isHydrating || !isSignedIn) {
      return;
    }
    if (!hasInitializedContributors.current) {
      hasInitializedContributors.current = true;
      const today = new Date().toISOString().split('T')[0];
      let needsUpdate = false;
      const updatedContributors = contributors.map(c => {
        if (c.oooStartDate && c.oooEndDate) {
          const shouldBeOOO = c.oooStartDate <= today && c.oooEndDate >= today;
          if (c.isOutOfOffice !== shouldBeOOO) {
            needsUpdate = true;
            return {
              ...c,
              isOutOfOffice: shouldBeOOO
            };
          }
        } else if (c.isOutOfOffice && !c.oooStartDate) {

          // Legacy: if isOutOfOffice is true but no dates, leave as-is (manual toggle)
        }
        return c;
      });
      if (needsUpdate) {
        setContributors(updatedContributors);
        return;
      }
    }
    if (shouldSkipNextSave.current) {
      shouldSkipNextSave.current = false;
      return;
    }
    localStorage.setItem(STORAGE_KEYS.CONTRIBUTORS, JSON.stringify(contributors));
    localStorage.setItem(STORAGE_KEYS.SOLUTIONS, JSON.stringify(solutions));
    localStorage.setItem(STORAGE_KEYS.AVAILABILITY, JSON.stringify(availability));
    localStorage.setItem(STORAGE_KEYS.DATE_AVAILABILITY, JSON.stringify(dateAvailability));
    localStorage.setItem(STORAGE_KEYS.EXPERIENCE_LEVELS, JSON.stringify(experienceLevels));
    setLastSaved(new Date());
    setShowSavedIndicator(true);
    const timer = setTimeout(() => setShowSavedIndicator(false), 2000);
    logStateToDatabase(contributors, solutions, availability, dateAvailability, experienceLevels);
    return () => clearTimeout(timer);
  }, [contributors, solutions, availability, dateAvailability, experienceLevels, isHydrating]);

  // Modal States
  const [isAddICOpen, setIsAddICOpen] = useState(false);
  const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isEditContributorOpen, setIsEditContributorOpen] = useState(false);
  const [isEditSolutionOpen, setIsEditSolutionOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [newICName, setNewICName] = useState('');
  const [newICRegion, setNewICRegion] = useState<Region>('USA');
  const [newSolutionName, setNewSolutionName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [importError, setImportError] = useState<string>('');
  const [editingContributor, setEditingContributor] = useState<Contributor | null>(null);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [editContributorName, setEditContributorName] = useState('');
  const [editContributorRegion, setEditContributorRegion] = useState<Region>('USA');
  const [editSolutionName, setEditSolutionName] = useState('');
  const [scheduleTarget, setScheduleTarget] = useState<{
    contributorId: string;
    solutionId: string;
  } | null>(null);
  // OOO modal state
  const [isOOOModalOpen, setIsOOOModalOpen] = useState(false);
  const [oooTargetContributorId, setOOOTargetContributorId] = useState<string | null>(null);
  const [oooStartDate, setOOOStartDate] = useState('');
  const [oooEndDate, setOOOEndDate] = useState('');
  const [oooError, setOOOError] = useState('');
  const requestManagementAccess = () => {
    if (!canManageData && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return canManageData;
  };
  const requestAdminAccess = () => {
    if (!isAdmin && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return isAdmin;
  };
  const handleAuthButtonClick = () => {
    if (isSignedIn) {
      void signOut();
      return;
    }
    void signIn();
  };
  useEffect(() => {
    if (canManageData) {
      if (isAdmin) {
        return;
      }
      setIsAddICOpen(false);
      setIsAddSolutionOpen(false);
      setIsImportOpen(false);
      return;
    }
    setIsAddICOpen(false);
    setIsAddSolutionOpen(false);
    setIsImportOpen(false);
    setIsEditContributorOpen(false);
    setIsEditSolutionOpen(false);
    setIsScheduleModalOpen(false);
    setIsOOOModalOpen(false);
  }, [canManageData, isAdmin]);
  // Download current data as CSV (for editing and re-importing)
  const handleDownloadCurrentData = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const rows = contributors.map(contributor => {
      const cells = solutions.map(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const level = experienceLevels[contributor.id]?.[solution.id];
        return level ? `${status}:${level}` : status;
      });
      return [contributor.name, contributor.region, ...cells].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `availability-data-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  // Download template CSV
  const handleDownloadTemplate = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const exampleRow = ['John Doe', 'USA', ...solutions.map((_, i) => {
      // Show varied examples
      if (i === 0) return 'available:L3';
      if (i === 1) return 'limited:L2';
      if (i === 2) return 'unavailable';
      return 'available:L1';
    })];
    const csvContent = [headers.join(','), exampleRow.join(','), ['Jane Smith', 'EMEA', ...solutions.map(() => '')].join(',')].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'availability-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  // Parse and import CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!requestAdminAccess()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          setImportError('File must contain at least a header row and one data row');
          return;
        }
        // Parse header
        const headers = lines[0].split(',').map(h => h.trim());
        const contributorIndex = headers.findIndex(h => h.toLowerCase() === 'contributor');
        const regionIndex = headers.findIndex(h => h.toLowerCase() === 'region');
        if (contributorIndex === -1 || regionIndex === -1) {
          setImportError('CSV must have "Contributor" and "Region" columns');
          return;
        }
        const solutionHeaders = headers.slice(2); // Everything after Contributor and Region
        // Create solutions from headers
        const newSolutions: Solution[] = solutionHeaders.map(name => ({
          // id: crypto.randomUUID(),
          id: stableSlugId(name.trim()),
          name: name.trim()
        }));
        // Parse data rows
        const newContributors: Contributor[] = [];
        const newAvailability: AvailabilityMap = {};
        const newExperienceLevels: ExperienceLevelMap = {};
        const validLevels = ['L1', 'L2', 'L3', 'L4'];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const name = values[contributorIndex];
          const region = values[regionIndex] as Region;
          if (!name || !REGIONS.includes(region)) continue;
          // const contributorId = crypto.randomUUID();
          const contributorId = stableSlugId(name);
          newContributors.push({
            id: contributorId,
            name,
            region
          });
          // Parse availability and experience level for each solution
          // Format: "status" or "status:level" (e.g., "available:L2")
          newAvailability[contributorId] = {};
          newExperienceLevels[contributorId] = {};
          solutionHeaders.forEach((_, idx) => {
            const cellValue = values[idx + 2]?.trim() || '';
            const solutionId = newSolutions[idx].id;
            const parts = cellValue.split(':').map(p => p.trim());
            const statusPart = parts[0]?.toLowerCase();
            const levelPart = parts[1]?.toUpperCase();
            // Parse status
            if (statusPart === 'available' || statusPart === 'limited' || statusPart === 'unavailable') {
              newAvailability[contributorId][solutionId] = statusPart as AvailabilityStatus;
            } else {
              newAvailability[contributorId][solutionId] = 'unavailable';
            }
            // Parse experience level
            if (levelPart && validLevels.includes(levelPart)) {
              newExperienceLevels[contributorId][solutionId] = levelPart as ExperienceLevel;
            }
          });
        }
        // Update state
        setContributors(newContributors);
        setSolutions(newSolutions);
        setAvailability(newAvailability);
        setExperienceLevels(newExperienceLevels);
        setImportError('');
        setIsImportOpen(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setImportError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parse error:', error);
      }
    };
    reader.readAsText(file);
  };
  // Helper to update lastUpdated timestamp for a contributor
  const touchContributor = (contributorId: string) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      lastUpdated: new Date().toISOString()
    } : c));
  };
  // Handlers
  const handleToggleAvailability = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    setAvailability(prev => {
      const contributorAvailability = prev[contributorId] || {};
      const currentStatus = contributorAvailability[solutionId] || 'unavailable';
      const statusCycle: Record<AvailabilityStatus, AvailabilityStatus> = {
        unavailable: 'limited',
        limited: 'available',
        available: 'unavailable'
      };
      const newStatus = statusCycle[currentStatus];
      // Log to Database
      if (contributor && solution) {
        logAvailabilityChange(contributor, solution, newStatus, experienceLevels[contributorId]?.[solutionId]);
      }
      return {
        ...prev,
        [contributorId]: {
          ...contributorAvailability,
          [solutionId]: newStatus
        }
      };
    });
    touchContributor(contributorId);
  };
  const handleCycleExperienceLevel = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    setExperienceLevels(prev => {
      const contributorLevels = prev[contributorId] || {};
      const currentLevel = contributorLevels[solutionId];
      const cycle: Record<string, ExperienceLevel | undefined> = {
        undefined: 'L1',
        L1: 'L2',
        L2: 'L3',
        L3: 'L4',
        L4: undefined as any
      };
      const newLevel = cycle[String(currentLevel)];
      const newContributorLevels = {
        ...contributorLevels
      };
      if (newLevel) {
        newContributorLevels[solutionId] = newLevel;
      } else {
        delete newContributorLevels[solutionId];
      }
      // Log to Database
      if (contributor && solution) {
        const currentStatus = availability[contributorId]?.[solutionId] || 'unavailable';
        logExperienceLevelChange(contributor, solution, newLevel, currentStatus);
      }
      return {
        ...prev,
        [contributorId]: newContributorLevels
      };
    });
    touchContributor(contributorId);
  };
  const handleUpdateContributorRegion = async (id: string, region: Region) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === id);
    if (contributor) {
      // Log to Database
      await logRegionUpdate(contributor, region);
    }
    setContributors(prev => prev.map(c => c.id === id ? {
      ...c,
      region
    } : c));
  };
  const handleAddContributor = () => {
    if (!requestAdminAccess()) return;
    if (!newICName.trim()) return;
    const newContributor: Contributor = {
      // id: crypto.randomUUID(),
      id: stableSlugId(newICName.trim()),
      name: newICName.trim(),
      region: newICRegion
    };
    setContributors([...contributors, newContributor]);
    setNewICName('');
    setNewICRegion('USA');
    setIsAddICOpen(false);
  };
  const handleAddSolution = () => {
    if (!requestAdminAccess()) return;
    if (!newSolutionName.trim()) return;
    const newSolution: Solution = {
      // id: crypto.randomUUID(),
      id: stableSlugId(newSolutionName.trim()),
      name: newSolutionName.trim()
    };
    setSolutions([...solutions, newSolution]);
    setNewSolutionName('');
    setIsAddSolutionOpen(false);
  };
  const handleDeleteContributor = (id: string) => {
    if (!requestManagementAccess()) return;
    if (confirm('Are you sure you want to remove this contributor?')) {
      setContributors(contributors.filter(c => c.id !== id));
    }
  };
  const handleDeleteSolution = (id: string) => {
    if (!requestManagementAccess()) return;
    if (confirm('Are you sure you want to remove this solution?')) {
      setSolutions(solutions.filter(s => s.id !== id));
    }
  };
  // Status filter handler
  const handleStatusFilterClick = (status: AvailabilityStatus) => {
    setSelectedStatusFilter(selectedStatusFilter === status ? null : status);
  };
  // Experience level filter handler
  const handleExperienceFilterClick = (level: ExperienceLevel) => {
    setSelectedExperienceFilter(selectedExperienceFilter === level ? null : level);
  };
  // Region filter handler
  const handleRegionFilterClick = (region: Region) => {
    setSelectedRegionFilter(selectedRegionFilter === region ? null : region);
  };
  // Filter handlers
  const handleContributorClick = (contributorId: string) => {
    setSelectedContributorId(selectedContributorId === contributorId ? null : contributorId);
  };
  const handleSolutionClick = (solutionId: string) => {
    setSelectedSolutionId(selectedSolutionId === solutionId ? null : solutionId);
  };
  const handleClearFilters = () => {
    setSelectedContributorId(null);
    setSelectedSolutionId(null);
    setSelectedStatusFilter(null);
    setSelectedExperienceFilter(null);
    setSelectedRegionFilter(null);
  };
  // Filtered views - apply search AND click filters
  const filteredContributors = contributors.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSolutions = solutions.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const hasContributorMatch = filteredContributors.length > 0;
  const hasSolutionMatch = filteredSolutions.length > 0;
  let displayContributors = searchQuery === '' ? contributors : hasContributorMatch ? filteredContributors : contributors;
  let displaySolutions = searchQuery === '' ? solutions : hasSolutionMatch ? filteredSolutions : solutions;
  // Apply click filters
  if (selectedContributorId) {
    displayContributors = displayContributors.filter(c => c.id === selectedContributorId);
  }
  if (selectedSolutionId) {
    displaySolutions = displaySolutions.filter(s => s.id === selectedSolutionId);
  }
  // Apply region filter
  if (selectedRegionFilter) {
    displayContributors = displayContributors.filter(c => c.region === selectedRegionFilter);
  }
  // Apply status + experience filters - supports multi-select (both can be active)
  let statusFilteredContributors = displayContributors;
  let statusFilteredSolutions = displaySolutions;
  if (selectedStatusFilter || selectedExperienceFilter) {
    const contributorIdsMatching = new Set<string>();
    const solutionIdsMatching = new Set<string>();
    displayContributors.forEach(contributor => {
      displaySolutions.forEach(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const expLevel = experienceLevels[contributor.id]?.[solution.id];
        const statusMatch = !selectedStatusFilter || status === selectedStatusFilter;
        const expMatch = !selectedExperienceFilter || expLevel === selectedExperienceFilter;
        if (statusMatch && expMatch) {
          contributorIdsMatching.add(contributor.id);
          solutionIdsMatching.add(solution.id);
        }
      });
    });
    statusFilteredContributors = displayContributors.filter(c => contributorIdsMatching.has(c.id));
    statusFilteredSolutions = displaySolutions.filter(s => solutionIdsMatching.has(s.id));
  }
  const selectedContributor = contributors.find(c => c.id === selectedContributorId);
  const selectedSolution = solutions.find(s => s.id === selectedSolutionId);
  const hasActiveFilters = selectedContributorId !== null || selectedSolutionId !== null || selectedStatusFilter !== null || selectedExperienceFilter !== null || selectedRegionFilter !== null;
  // Reorder handlers
  const handleReorderContributors = (fromIndex: number, toIndex: number) => {
    if (!requestManagementAccess()) return;
    const newContributors = [...contributors];
    const [removed] = newContributors.splice(fromIndex, 1);
    newContributors.splice(toIndex, 0, removed);
    setContributors(newContributors);
  };
  const handleReorderSolutions = (fromIndex: number, toIndex: number) => {
    if (!requestManagementAccess()) return;
    const newSolutions = [...solutions];
    const [removed] = newSolutions.splice(fromIndex, 1);
    newSolutions.splice(toIndex, 0, removed);
    setSolutions(newSolutions);
  };
  // Edit handlers
  const handleEditContributor = (contributor: Contributor) => {
    if (!requestManagementAccess()) return;
    setEditingContributor(contributor);
    setEditContributorName(contributor.name);
    setEditContributorRegion(contributor.region);
    setIsEditContributorOpen(true);
  };
  const handleSaveContributor = async () => {
    if (!requestManagementAccess()) return;
    if (!editingContributor || !editContributorName.trim()) return;
    // Log to Database
    await logContributorEdit(editingContributor.id, editingContributor.name, editContributorName.trim(), editingContributor.region, editContributorRegion);
    setContributors(prev => prev.map(c => c.id === editingContributor.id ? {
      ...c,
      name: editContributorName.trim(),
      region: editContributorRegion
    } : c));
    setIsEditContributorOpen(false);
    setEditingContributor(null);
    setEditContributorName('');
  };
  const handleEditSolution = (solution: Solution) => {
    if (!requestManagementAccess()) return;
    setEditingSolution(solution);
    setEditSolutionName(solution.name);
    setIsEditSolutionOpen(true);
  };
  const handleSaveSolution = async () => {
    if (!requestManagementAccess()) return;
    if (!editingSolution || !editSolutionName.trim()) return;
    // Log to Database
    await logSolutionEdit(editingSolution.id, editingSolution.name, editSolutionName.trim());
    setSolutions(prev => prev.map(s => s.id === editingSolution.id ? {
      ...s,
      name: editSolutionName.trim()
    } : s));
    setIsEditSolutionOpen(false);
    setEditingSolution(null);
    setEditSolutionName('');
  };
  // OOO handler - now opens modal instead of toggling
  const handleOpenOOOModal = (contributorId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    setOOOTargetContributorId(contributorId);
    setOOOStartDate(contributor?.oooStartDate || '');
    setOOOEndDate(contributor?.oooEndDate || '');
    setOOOError('');
    setIsOOOModalOpen(true);
  };
  const handleSaveOOO = async () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    // If both dates are empty, clear OOO
    if (!oooStartDate && !oooEndDate) {
      if (contributor) {
        await logOOOSchedule(contributor, '', '', true);
      }
      setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
        ...c,
        isOutOfOffice: false,
        oooStartDate: undefined,
        oooEndDate: undefined
      } : c));
      setIsOOOModalOpen(false);
      return;
    }
    if (!oooStartDate || !oooEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    const start = new Date(oooStartDate);
    const end = new Date(oooEndDate);
    if (end < start) {
      setOOOError('End date must be on or after start date');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const isCurrentlyOOO = oooStartDate <= today && oooEndDate >= today;
    // Log to Database
    if (contributor) {
      await logOOOSchedule(contributor, oooStartDate, oooEndDate);
    }
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: isCurrentlyOOO,
      oooStartDate,
      oooEndDate
    } : c));
    setIsOOOModalOpen(false);
  };
  const handleClearOOO = async () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    // Log to Database
    if (contributor) {
      await logOOOSchedule(contributor, '', '', true);
    }
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: false,
      oooStartDate: undefined,
      oooEndDate: undefined
    } : c));
    setIsOOOModalOpen(false);
  };
  // Date Availability Handlers
  const handleOpenSchedule = (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    setScheduleTarget({
      contributorId,
      solutionId
    });
    setIsScheduleModalOpen(true);
  };
  const handleAddSchedule = async (schedule: Omit<DateAvailability, 'id'>) => {
    if (!requestManagementAccess()) return;
    if (!scheduleTarget) return;
    const {
      contributorId,
      solutionId
    } = scheduleTarget;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const newSchedule: DateAvailability = {
      ...schedule,
      // id: crypto.randomUUID()
      id: stableSlugId(schedule.startDate + schedule.endDate + schedule.status)
    };
    // Log to Database
    if (contributor && solution) {
      await logDateOverride(contributor, solution, schedule.startDate, schedule.endDate, schedule.status);
    }
    setDateAvailability(prev => {
      const contributorSchedules = prev[contributorId] || {};
      const currentSchedules = contributorSchedules[solutionId] || [];
      return {
        ...prev,
        [contributorId]: {
          ...contributorSchedules,
          [solutionId]: [...currentSchedules, newSchedule]
        }
      };
    });
    touchContributor(contributorId);
  };
  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!requestManagementAccess()) return;
    if (!scheduleTarget) return;
    const {
      contributorId,
      solutionId
    } = scheduleTarget;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    // Find the schedule being deleted to log its details
    const scheduleToDelete = dateAvailability[contributorId]?.[solutionId]?.find(s => s.id === scheduleId);
    // Log to Database
    if (contributor && solution && scheduleToDelete) {
      await logDateOverride(contributor, solution, scheduleToDelete.startDate, scheduleToDelete.endDate, scheduleToDelete.status, true);
    }
    setDateAvailability(prev => {
      const contributorSchedules = prev[contributorId] || {};
      const currentSchedules = contributorSchedules[solutionId] || [];
      return {
        ...prev,
        [contributorId]: {
          ...contributorSchedules,
          [solutionId]: currentSchedules.filter(s => s.id !== scheduleId)
        }
      };
    });
    touchContributor(contributorId);
  };
  // Helper to get current schedules for the modal
  const getCurrentSchedules = () => {
    if (!scheduleTarget) return [];
    const {
      contributorId,
      solutionId
    } = scheduleTarget;
    return dateAvailability[contributorId]?.[solutionId] || [];
  };
  // Helper to get names for the modal
  const getScheduleNames = () => {
    if (!scheduleTarget) return {
      contributorName: '',
      solutionName: ''
    };
    const contributor = contributors.find(c => c.id === scheduleTarget.contributorId);
    const solution = solutions.find(s => s.id === scheduleTarget.solutionId);
    return {
      contributorName: contributor?.name || '',
      solutionName: solution?.name || ''
    };
  };
  // Calculate effective availability for display
  // This merges base availability with date-based overrides for TODAY
  const getEffectiveAvailability = () => {
    const effective = {
      ...availability
    };
    const today = new Date().toISOString().split('T')[0];
    Object.entries(dateAvailability).forEach(([contributorId, solutionMap]) => {
      Object.entries(solutionMap).forEach(([solutionId, schedules]) => {
        const activeSchedule = schedules.find(s => s.startDate <= today && s.endDate >= today);
        if (activeSchedule) {
          if (!effective[contributorId]) effective[contributorId] = {};
          effective[contributorId][solutionId] = activeSchedule.status;
        }
      });
    });
    return effective;
  };
  const effectiveAvailability = getEffectiveAvailability();
  const accessLevelLabel = accessLevel === 'admin' ? 'Admin' : accessLevel === 'editor' ? 'Editor' : accessLevel === 'viewer' ? 'Viewer' : 'Signed out';
  if (isHydrating) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-52">
        {isAuthReady ? 'Loading resource data...' : 'Initializing Adobe sign-in...'}
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-53">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-54">
          <div className="flex items-center gap-4" data-id="element-55">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-56" />
            <div data-id="element-57">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-58">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-59">
                Sign in with Adobe ID
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-60">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-61">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-62" /> <br data-id="element-63" />
              <p data-id="element-64">
              1. Click Sign-In <br data-id="element-65" />
              2. Login using your company account <br data-id="element-66" />
              3. Choose profile : Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-67">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="primary" size="lg" isLoading={isAuthLoading} data-id="element-68">
              
              <LogIn className="mr-2 h-4 w-4" data-id="element-69" />
              Sign in with Adobe ID
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-screen bg-slate-50 text-slate-900 font-sans" data-id="element-70">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm z-30" data-id="element-71">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-72">
          <div className="flex items-center gap-3" data-id="element-73">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-12 w-auto" data-id="element-74" />
            
            <div data-id="element-75">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-76">
                Resource Availability Dashboard
              </h1>
              <div className="flex items-center gap-2" data-id="element-77">
                <p className="text-sm text-slate-500" data-id="element-78">
                  Track solution coverage and individual capacity
                </p>
                {showSavedIndicator && <div className="flex items-center gap-1 text-xs text-green-600 animate-in fade-in duration-200" data-id="element-79">
                    <Check className="h-3 w-3" data-id="element-80" />
                    <span data-id="element-81">Saved</span>
                  </div>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-82">
            <div className="relative hidden md:block" data-id="element-83">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-84" />
              <input type="text" placeholder="Search ICs or solutions..." className="pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-85" />
              
            </div>
            <div className="h-8 w-px bg-slate-200 mx-2 hidden md:block" data-id="element-86" />

            {isSignedIn && <div className="hidden lg:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-87">
                <span className={`h-2 w-2 rounded-full ${canManageData ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-88" />
                <span className="font-medium text-slate-800" data-id="element-89">
                  {user?.displayName || 'Adobe user'}
                </span>
                {user?.email && <span className="text-slate-500" data-id="element-90">{user.email}</span>}
                <span className={`rounded-full px-2 py-0.5 font-medium ${accessLevel === 'admin' ? 'bg-slate-900 text-white' : accessLevel === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-91">
                  
                  {accessLevelLabel}
                </span>
              </div>}

            <Button onClick={handleAuthButtonClick} variant={isSignedIn ? 'outline' : 'primary'} size="sm" isLoading={isAuthLoading && !isSignedIn} data-id="element-92">
              
              {isSignedIn ? <LogOut className="h-4 w-4 mr-2" data-id="element-93" /> : <LogIn className="h-4 w-4 mr-2" data-id="element-94" />}
              {isSignedIn ? 'Sign Out' : 'Sign in with Adobe ID'}
            </Button>

            {isAdmin && <>
                <Button onClick={handleDownloadCurrentData} variant="outline" size="sm" data-id="element-95">
                
                  <Download className="h-4 w-4 mr-2" data-id="element-96" />
                  Export Data
                </Button>
                <Button onClick={() => setIsImportOpen(true)} variant="outline" size="sm" data-id="element-97">
                
                  <Upload className="h-4 w-4 mr-2" data-id="element-98" />
                  Import
                </Button>
                <Button onClick={() => setIsAddICOpen(true)} variant="outline" size="sm" data-id="element-99">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-100" />
                  Add Contributor
                </Button>
                <Button onClick={() => setIsAddSolutionOpen(true)} variant="primary" size="sm" data-id="element-101">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-102" />
                  Add Solution
                </Button>
              </>}
          </div>
        </div>

        {/* Mobile Search */}
        <div className="mt-4 md:hidden relative" data-id="element-103">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-104" />
          <input type="text" placeholder="Search ICs or solutions..." className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000]" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-105" />
          
        </div>

        {(authError || isAuthReady && !isSignedIn || isSignedIn && !canManageData || isSignedIn && canManageData && !isAdmin) && <div className="mt-4 space-y-2" data-id="element-106">
            {authError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-107">
                {authError}
              </div>}
            {isAuthReady && !isSignedIn && !authError && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-108">
                Sign in with Adobe ID to unlock
              </div>}
            {isSignedIn && !canManageData && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-id="element-109">
                Signed in as{' '}
                <span className="font-medium" data-id="element-110">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                This account has viewer access only.
              </div>}
            {isSignedIn && canManageData && !isAdmin && <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-id="element-111">
                Signed in as{' '}
                <span className="font-medium" data-id="element-112">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                
              </div>}
          </div>}
      </header>

      {/* Legend & Active Filters */}
      <div className="bg-white border-b border-slate-200 px-6 py-2" data-id="element-113">
        <div className="flex flex-col gap-2" data-id="element-114">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-115">
            <div className="flex items-center gap-4 text-xs" data-id="element-116">
              <span className="font-medium text-slate-600" data-id="element-117">Status:</span>
              {/* Clickable status filters */}
              <button onClick={() => handleStatusFilterClick('available')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'available' ? 'bg-green-100 ring-2 ring-green-500 ring-offset-1' : 'hover:bg-green-50'}`} title="Click to filter by Qualified and Available" data-id="element-118">
                
                <div className="h-3 w-3 rounded-full bg-green-500" data-id="element-119"></div>
                <span className={selectedStatusFilter === 'available' ? 'text-green-700 font-medium' : 'text-slate-700'} data-id="element-120">
                  
                  Qualified and Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('limited')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'limited' ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-offset-1' : 'hover:bg-yellow-50'}`} title="Click to filter by Qualified not Available" data-id="element-121">
                
                <div className="h-3 w-3 rounded-full bg-yellow-400" data-id="element-122"></div>
                <span className={selectedStatusFilter === 'limited' ? 'text-yellow-700 font-medium' : 'text-slate-700'} data-id="element-123">
                  
                  Qualified not Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('unavailable')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'unavailable' ? 'bg-red-100 ring-2 ring-red-500 ring-offset-1' : 'hover:bg-red-50'}`} title="Click to filter by Not Qualified" data-id="element-124">
                
                <div className="h-3 w-3 rounded-full bg-red-500" data-id="element-125"></div>
                <span className={selectedStatusFilter === 'unavailable' ? 'text-red-700 font-medium' : 'text-slate-700'} data-id="element-126">
                  
                  Not Qualified
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-127" />

              <span className="font-medium text-slate-600" data-id="element-128">Level:</span>
              {/* Clickable experience level filters */}
              <button onClick={() => handleExperienceFilterClick('L1')} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedExperienceFilter === 'L1' ? 'bg-slate-200 ring-2 ring-slate-400 ring-offset-1' : 'hover:bg-slate-50'}`} title="Click to filter by L1 - Foundational" data-id="element-129">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200" data-id="element-130">
                  L1
                </span>
                <span className={selectedExperienceFilter === 'L1' ? 'text-slate-800 font-medium' : 'text-slate-700'} data-id="element-131">
                  
                  Foundational
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L2')} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedExperienceFilter === 'L2' ? 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1' : 'hover:bg-blue-50'}`} title="Click to filter by L2 - Intermediate" data-id="element-132">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-blue-50 text-blue-600 border-blue-200" data-id="element-133">
                  L2
                </span>
                <span className={selectedExperienceFilter === 'L2' ? 'text-blue-700 font-medium' : 'text-slate-700'} data-id="element-134">
                  
                  Intermediate
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L3')} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedExperienceFilter === 'L3' ? 'bg-indigo-100 ring-2 ring-indigo-400 ring-offset-1' : 'hover:bg-indigo-50'}`} title="Click to filter by L3 - Advanced" data-id="element-135">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-indigo-50 text-indigo-600 border-indigo-200" data-id="element-136">
                  L3
                </span>
                <span className={selectedExperienceFilter === 'L3' ? 'text-indigo-700 font-medium' : 'text-slate-700'} data-id="element-137">
                  
                  Advanced
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L4')} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedExperienceFilter === 'L4' ? 'bg-violet-100 ring-2 ring-violet-400 ring-offset-1' : 'hover:bg-violet-50'}`} title="Click to filter by L4 - Champion" data-id="element-138">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200" data-id="element-139">
                  L4
                </span>
                <span className={selectedExperienceFilter === 'L4' ? 'text-violet-700 font-medium' : 'text-slate-700'} data-id="element-140">
                  
                  Champion
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-141" />

              <span className="font-medium text-slate-600" data-id="element-142">Region:</span>
              {REGIONS.map(region => {
              const isActive = selectedRegionFilter === region;
              const colorMap: Record<Region, {
                active: string;
                hover: string;
                text: string;
                dot: string;
              }> = {
                USA: {
                  active: 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1',
                  hover: 'hover:bg-blue-50',
                  text: 'text-blue-700 font-medium',
                  dot: 'bg-blue-500'
                },
                LATAM: {
                  active: 'bg-amber-100 ring-2 ring-amber-400 ring-offset-1',
                  hover: 'hover:bg-amber-50',
                  text: 'text-amber-700 font-medium',
                  dot: 'bg-amber-500'
                },
                India: {
                  active: 'bg-emerald-100 ring-2 ring-emerald-400 ring-offset-1',
                  hover: 'hover:bg-emerald-50',
                  text: 'text-emerald-700 font-medium',
                  dot: 'bg-emerald-500'
                },
                EMEA: {
                  active: 'bg-purple-100 ring-2 ring-purple-400 ring-offset-1',
                  hover: 'hover:bg-purple-50',
                  text: 'text-purple-700 font-medium',
                  dot: 'bg-purple-500'
                }
              };
              const colors = colorMap[region];
              return <button key={region} onClick={() => handleRegionFilterClick(region)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${isActive ? colors.active : colors.hover}`} title={`Click to filter by ${region}`} data-id="element-143">
                    
                    <div className={`h-2.5 w-2.5 rounded-sm ${colors.dot}`} data-id="element-144" />
                    <span className={isActive ? colors.text : 'text-slate-700'} data-id="element-145">
                      {region}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && <div className="flex items-center gap-2" data-id="element-146">
              <span className="text-xs font-medium text-slate-600" data-id="element-147">
                Filtered:
              </span>
              {selectedStatusFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${STATUS_COLORS[selectedStatusFilter].bg} ${STATUS_COLORS[selectedStatusFilter].text}`} data-id="element-148">
              
                  {STATUS_LABELS[selectedStatusFilter]}
                  <button onClick={() => setSelectedStatusFilter(null)} className={`hover:${STATUS_COLORS[selectedStatusFilter].bg} rounded-full p-0.5`} data-id="element-149">
                
                    <X className="h-3 w-3" data-id="element-150" />
                  </button>
                </span>}
              {selectedExperienceFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedExperienceFilter === 'L1' ? 'bg-slate-100 text-slate-700' : selectedExperienceFilter === 'L2' ? 'bg-blue-100 text-blue-700' : selectedExperienceFilter === 'L3' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'}`} data-id="element-151">
              
                  {selectedExperienceFilter} –{' '}
                  {EXPERIENCE_LABELS[selectedExperienceFilter]}
                  <button onClick={() => setSelectedExperienceFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-152">
                
                    <X className="h-3 w-3" data-id="element-153" />
                  </button>
                </span>}
              {selectedRegionFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedRegionFilter === 'USA' ? 'bg-blue-100 text-blue-700' : selectedRegionFilter === 'LATAM' ? 'bg-amber-100 text-amber-700' : selectedRegionFilter === 'India' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-154">
              
                  {selectedRegionFilter}
                  <button onClick={() => setSelectedRegionFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-155">
                
                    <X className="h-3 w-3" data-id="element-156" />
                  </button>
                </span>}
              {(selectedStatusFilter || selectedExperienceFilter || selectedRegionFilter) && [selectedStatusFilter, selectedExperienceFilter, selectedRegionFilter].filter(Boolean).length > 1 && <span className="text-[10px] text-slate-400 italic" data-id="element-157">
                    (combined filters)
                  </span>}
              {selectedContributor && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" data-id="element-158">
                  {selectedContributor.name}
                  <button onClick={() => setSelectedContributorId(null)} className="hover:bg-blue-200 rounded-full p-0.5" data-id="element-159">
                
                    <X className="h-3 w-3" data-id="element-160" />
                  </button>
                </span>}
              {selectedSolution && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs" data-id="element-161">
                  {selectedSolution.name}
                  <button onClick={() => setSelectedSolutionId(null)} className="hover:bg-purple-200 rounded-full p-0.5" data-id="element-162">
                
                    <X className="h-3 w-3" data-id="element-163" />
                  </button>
                </span>}
              <button onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-164">
              
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden p-4 md:p-6" data-id="element-165">
        <AvailabilityGrid contributors={statusFilteredContributors} solutions={statusFilteredSolutions} availability={effectiveAvailability} dateAvailability={dateAvailability} onToggleAvailability={handleToggleAvailability} onDeleteContributor={handleDeleteContributor} onDeleteSolution={handleDeleteSolution} onUpdateContributorRegion={handleUpdateContributorRegion} onContributorClick={handleContributorClick} onSolutionClick={handleSolutionClick} selectedContributorId={selectedContributorId} selectedSolutionId={selectedSolutionId} selectedStatusFilter={selectedStatusFilter} selectedExperienceFilter={selectedExperienceFilter} onReorderContributors={handleReorderContributors} onReorderSolutions={handleReorderSolutions} isAdminAuthenticated={canManageData} allContributors={contributors} allSolutions={solutions} onEditContributor={handleEditContributor} onEditSolution={handleEditSolution} onToggleOutOfOffice={handleOpenOOOModal} onOpenSchedule={handleOpenSchedule} experienceLevels={experienceLevels} onCycleExperienceLevel={handleCycleExperienceLevel} data-id="element-166" />
        
      </main>

      {/* Import Modal */}
      <Modal isOpen={isImportOpen} onClose={() => {
      setIsImportOpen(false);
      setImportError('');
    }} title="Import Availability Data" footer={<div className="flex justify-between items-center w-full" data-id="element-168">
            <div className="flex gap-2" data-id="element-169">
              <Button variant="outline" onClick={handleDownloadTemplate} size="sm" data-id="element-170">
              
                <FileSpreadsheet className="h-4 w-4 mr-2" data-id="element-171" />
                Blank Template
              </Button>
              <Button variant="outline" onClick={handleDownloadCurrentData} size="sm" data-id="element-172">
              
                <Download className="h-4 w-4 mr-2" data-id="element-173" />
                Current Data
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setIsImportOpen(false)} data-id="element-174">
              Close
            </Button>
          </div>} data-id="element-167">
        
        <div className="space-y-4" data-id="element-175">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-176">
            <h4 className="font-medium text-blue-900 text-sm mb-2" data-id="element-177">
              CSV Format Instructions
            </h4>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside" data-id="element-178">
              <li data-id="element-179">
                First column: <strong data-id="element-180">Contributor</strong> (name)
              </li>
              <li data-id="element-181">
                Second column: <strong data-id="element-182">Region</strong> (USA, LATAM, India, or
                EMEA)
              </li>
              <li data-id="element-183">Remaining columns: Solution names</li>
              <li data-id="element-184">
                Cell format:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-185">status</code> or{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-186">status:level</code>
              </li>
              <li data-id="element-187">
                Status values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-188">available</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-189">limited</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-190">unavailable</code>
              </li>
              <li data-id="element-191">
                Level values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-192">L1</code>{' '}
                (Foundational),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-193">L2</code>{' '}
                (Intermediate),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-194">L3</code> (Advanced),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-195">L4</code> (Champion)
              </li>
              <li data-id="element-196">
                Examples:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-197">available:L3</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-198">limited:L1</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-199">unavailable</code>
              </li>
              <li data-id="element-200">
                Leave cells empty to default to "unavailable" with no level
              </li>
            </ul>
          </div>

          {importError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-id="element-201">
              {importError}
            </div>}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors" data-id="element-202">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="csv-upload" data-id="element-203" />
            
            <label htmlFor="csv-upload" className="cursor-pointer" data-id="element-204">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" data-id="element-205" />
              <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-206">
                Click to upload CSV file
              </p>
              <p className="text-xs text-slate-500" data-id="element-207">or drag and drop</p>
            </label>
          </div>

          <p className="text-xs text-slate-500" data-id="element-208">
            <strong data-id="element-209">Note:</strong> Importing will replace all current data.
            Download your current data first if you want to keep it.
          </p>
        </div>
      </Modal>

      {/* Add Contributor Modal */}
      <Modal isOpen={isAddICOpen} onClose={() => setIsAddICOpen(false)} title="Add New Contributor" footer={<div className="flex justify-end gap-3" data-id="element-211">
            <Button variant="ghost" onClick={() => setIsAddICOpen(false)} data-id="element-212">
              Cancel
            </Button>
            <Button onClick={handleAddContributor} data-id="element-213">Add Contributor</Button>
          </div>} data-id="element-210">
        
        <div className="space-y-4" data-id="element-214">
          <p className="text-sm text-slate-500" data-id="element-215">
            Enter the name and region of the individual contributor you would
            like to add to the dashboard.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={newICName} onChange={e => setNewICName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-216" />
          
          <div data-id="element-217">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-218">
              Region
            </label>
            <select value={newICRegion} onChange={e => setNewICRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-219">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-220">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Add Solution Modal */}
      <Modal isOpen={isAddSolutionOpen} onClose={() => setIsAddSolutionOpen(false)} title="Add New Solution" footer={<div className="flex justify-end gap-3" data-id="element-222">
            <Button variant="ghost" onClick={() => setIsAddSolutionOpen(false)} data-id="element-223">
              Cancel
            </Button>
            <Button onClick={handleAddSolution} data-id="element-224">Add Solution</Button>
          </div>} data-id="element-221">
        
        <div className="space-y-4" data-id="element-225">
          <p className="text-sm text-slate-500" data-id="element-226">
            Enter the name of the new Adobe solution or product area.
          </p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={newSolutionName} onChange={e => setNewSolutionName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddSolution()} data-id="element-227" />
          
        </div>
      </Modal>

      {/* Edit Contributor Modal */}
      <Modal isOpen={isEditContributorOpen} onClose={() => {
      setIsEditContributorOpen(false);
      setEditingContributor(null);
      setEditContributorName('');
    }} title="Edit Contributor" footer={<div className="flex justify-end gap-3" data-id="element-229">
            <Button variant="ghost" onClick={() => {
        setIsEditContributorOpen(false);
        setEditingContributor(null);
        setEditContributorName('');
      }} data-id="element-230">
            
              Cancel
            </Button>
            <Button onClick={handleSaveContributor} data-id="element-231">Save Changes</Button>
          </div>} data-id="element-228">
        
        <div className="space-y-4" data-id="element-232">
          <p className="text-sm text-slate-500" data-id="element-233">
            Update the contributor's name and region.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={editContributorName} onChange={e => setEditContributorName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveContributor()} data-id="element-234" />
          
          <div data-id="element-235">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-236">
              Region
            </label>
            <select value={editContributorRegion} onChange={e => setEditContributorRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-237">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-238">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Edit Solution Modal */}
      <Modal isOpen={isEditSolutionOpen} onClose={() => {
      setIsEditSolutionOpen(false);
      setEditingSolution(null);
      setEditSolutionName('');
    }} title="Edit Solution" footer={<div className="flex justify-end gap-3" data-id="element-240">
            <Button variant="ghost" onClick={() => {
        setIsEditSolutionOpen(false);
        setEditingSolution(null);
        setEditSolutionName('');
      }} data-id="element-241">
            
              Cancel
            </Button>
            <Button onClick={handleSaveSolution} data-id="element-242">Save Changes</Button>
          </div>} data-id="element-239">
        
        <div className="space-y-4" data-id="element-243">
          <p className="text-sm text-slate-500" data-id="element-244">Update the solution name.</p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={editSolutionName} onChange={e => setEditSolutionName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveSolution()} data-id="element-245" />
          
        </div>
      </Modal>

      {/* OOO Date Range Modal */}
      <Modal isOpen={isOOOModalOpen} onClose={() => setIsOOOModalOpen(false)} title="Set Out of Office" footer={<div className="flex justify-between w-full" data-id="element-247">
            {oooStartDate || oooEndDate ? <Button variant="danger" size="sm" onClick={handleClearOOO} data-id="element-248">
                Clear OOO
              </Button> : <div data-id="element-249" />}
            <div className="flex gap-3" data-id="element-250">
              <Button variant="ghost" onClick={() => setIsOOOModalOpen(false)} data-id="element-251">
                Cancel
              </Button>
              <Button onClick={handleSaveOOO} data-id="element-252">Save</Button>
            </div>
          </div>} data-id="element-246">
        
        <div className="space-y-4" data-id="element-253">
          {(() => {
          const contributor = contributors.find(c => c.id === oooTargetContributorId);
          return contributor ? <div className="bg-slate-50 p-3 rounded-md border border-slate-200" data-id="element-254">
                <p className="text-sm text-slate-600" data-id="element-255">
                  Setting Out of Office for{' '}
                  <span className="font-semibold text-slate-900" data-id="element-256">
                    {contributor.name}
                  </span>
                </p>
              </div> : null;
        })()}

          <p className="text-sm text-slate-500" data-id="element-257">
            Select a date range for when you'll be out of office. Your entire
            column will appear dimmed during this period.
          </p>

          <div className="grid grid-cols-2 gap-4" data-id="element-258">
            <Input type="date" label="Start Date" value={oooStartDate} onChange={e => setOOOStartDate(e.target.value)} data-id="element-259" />
            
            <Input type="date" label="End Date" value={oooEndDate} onChange={e => setOOOEndDate(e.target.value)} min={oooStartDate || undefined} data-id="element-260" />
            
          </div>

          {oooError && <p className="text-sm text-red-600" data-id="element-261">{oooError}</p>}

          {oooStartDate && oooEndDate && (() => {
          const today = new Date().toISOString().split('T')[0];
          const isActive = oooStartDate <= today && oooEndDate >= today;
          const isFuture = oooStartDate > today;
          return <div className={`p-3 rounded-md border text-sm ${isActive ? 'bg-orange-50 border-orange-200 text-orange-700' : isFuture ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-id="element-262">
                
                  {isActive && '⚡ OOO is currently active'}
                  {isFuture && `📅 OOO scheduled: ${new Date(oooStartDate).toLocaleDateString()} – ${new Date(oooEndDate).toLocaleDateString()}`}
                  {!isActive && !isFuture && 'This date range is in the past'}
                </div>;
        })()}
        </div>
      </Modal>

      {/* Date Availability Modal */}
      {scheduleTarget && <DateAvailabilityModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} contributorName={getScheduleNames().contributorName} solutionName={getScheduleNames().solutionName} schedules={getCurrentSchedules()} onAddSchedule={handleAddSchedule} onDeleteSchedule={handleDeleteSchedule} data-id="element-263" />}
    </div>;
}